import React, { useState, useEffect } from 'react';
import { FaGift, FaSync } from 'react-icons/fa';
import './IBRewards.css';

const API_URL = "https://server.majestyfx.com/admin_fetch_lots_users.php";

const IBRewards = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setRewards(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to fetch IB rewards');
      }
    } catch (err) {
      console.error('Error fetching IB rewards:', err);
      setError(err.message || 'Failed to load IB rewards');
      setRewards([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter rewards based on search term
  const filteredRewards = rewards.filter(reward =>
    (reward.user_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (reward.trade_id || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRewards = filteredRewards.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRewards.length / itemsPerPage);

  // Calculate totals
  const totalLotsDone = rewards.reduce((sum, r) => sum + parseFloat(r.lots_done || 0), 0);
  const totalRewardAmount = rewards.reduce((sum, r) => sum + parseFloat(r.reward_amount || 0), 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="ib-rewards-container">
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">
            <FaGift className="title-icon" />
            IB Rewards
          </h1>
          <p className="page-subtitle">View all IB lot rewards and commissions</p>
        </div>
        <button 
          className="refresh-btn"
          onClick={fetchRewards}
          disabled={loading}
        >
          <FaSync className={loading ? 'spinning' : ''} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-icon users">
            <FaGift />
          </div>
          <div className="stat-info">
            <h3>Total Records</h3>
            <p className="stat-value">{rewards.length}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon lots">
            <FaGift />
          </div>
          <div className="stat-info">
            <h3>Total Lots Done</h3>
            <p className="stat-value">{totalLotsDone.toFixed(2)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon rewards">
            <FaGift />
          </div>
          <div className="stat-info">
            <h3>Total Rewards</h3>
            <p className="stat-value">${totalRewardAmount.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={fetchRewards} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      {/* Search */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search by email or trade ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          disabled={loading}
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading IB rewards...</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="rewards-table">
            <thead>
              <tr>
                <th>#</th>
                <th>ID</th>
                <th>User Email</th>
                <th>Trade ID</th>
                <th>Lots Done</th>
                <th>Reward Amount</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {currentRewards.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">No rewards found</td>
                </tr>
              ) : (
                currentRewards.map((reward, index) => (
                  <tr key={reward.id}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>{reward.id}</td>
                    <td className="email-cell">{reward.user_email}</td>
                    <td className="trade-id-cell">{reward.trade_id}</td>
                    <td className="lots-cell">{parseFloat(reward.lots_done).toFixed(2)}</td>
                    <td className="reward-cell">${parseFloat(reward.reward_amount).toFixed(2)}</td>
                    <td className="date-cell">{formatDate(reward.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && filteredRewards.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredRewards.length)} of {filteredRewards.length} records
          </div>
          <div className="pagination-controls">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            
            <div className="page-numbers">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IBRewards;
